import { API_URL } from './config';

const API_BASE_URL = API_URL;

export interface Distributor {
  id: number;
  name: string;
  brand?: string;
  sales_channel?: string;
  points?: number;
  team?: string;
  date_added?: string;
  created_at?: string;
  updated_at?: string;
  is_archived: boolean;
  date_archived?: string | null;
  archived_by?: number | null;
  archived_by_username?: string | null;
}

export interface PaginatedDistributorsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Distributor[];
}

export interface DashboardStats {
  total_requests: number;
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  processed_count: number;
  not_processed_count: number;
  cancelled_count: number;
  on_board_count: number;
  customers_count: number;
}

export interface RedemptionRequest {
  id: number;
  requested_by: number;
  requested_by_name: string;
  requested_for: number;
  requested_for_name: string;
  team: number | null;
  team_name: string | null;
  points_deducted_from: string;
  points_deducted_from_display: string;
  total_points: number;
  status: string;
  status_display: string;
  processing_status: string;
  processing_status_display: string;
  date_requested: string;
  reviewed_by: number | null;
  reviewed_by_name: string | null;
  date_reviewed: string | null;
  processed_by: number | null;
  processed_by_name: string | null;
  date_processed: string | null;
  cancelled_by: number | null;
  cancelled_by_name: string | null;
  date_cancelled: string | null;
  remarks: string | null;
  rejection_reason: string | null;
  items: any[];
}

export interface PaginatedResponse<T> {
  count: number;
  limit: number;
  offset: number;
  results: T[];
}

export interface BatchUpdateResponse {
  message: string;
  updated_count: number;
  failed_count: number;
  updated_ids: number[];
  failed?: { id: number; error: string }[] | null;
}

export interface ChunkedUpdateProgress {
  currentChunk: number;
  totalChunks: number;
  processedRecords: number;
  totalRecords: number;
  successCount: number;
  failedCount: number;
}

export interface ChunkedUpdateResult {
  success: boolean;
  totalUpdated: number;
  totalFailed: number;
  allUpdatedIds: number[];
  allFailed: { id: number; error: string }[];
  partialSuccess: boolean;
}

export interface SalesVolumeTier {
  level: number;
  lower: number;
  upper: number;
  rate: number;
}

export interface SalesVolumeAllocationResponse {
  message: string;
  updated_count: number;
  failed_count: number;
  allocations: {
    id: number;
    name: string;
    sales_volume: number;
    rate: number;
    points_added: number;
    new_total: number;
  }[];
  failed?: { id: number; error: string }[] | null;
}

export const distributorsApi = {
  getDistributorsPage: async (page: number = 1, pageSize: number = 20, searchQuery: string = '', showArchived?: boolean): Promise<PaginatedDistributorsResponse> => {
    const url = new URL(`${API_BASE_URL}/distributors/`, window.location.origin);
    url.searchParams.append('page', page.toString());
    url.searchParams.append('page_size', pageSize.toString());
    if (searchQuery) {
      url.searchParams.append('search', searchQuery);
    }
    if (showArchived) {
      url.searchParams.append('show_archived', 'true');
    }
    const response = await fetch(url.toString(), {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch distributors');
    const data = await response.json();
    // Ensure we return paginated format
    if (Array.isArray(data)) {
      return { count: data.length, next: null, previous: null, results: data };
    }
    return data;
  },

  batchUpdatePoints: async (updates: { id: number; points: number }[], reason?: string): Promise<BatchUpdateResponse> => {
    const response = await fetch(`${API_BASE_URL}/distributors/batch_update_points/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ updates, reason: reason || '' }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to batch update points');
    }
    
    return data;
  },

  batchUpdatePointsChunked: async (
    updates: { id: number; points: number }[],
    onProgress?: (progress: ChunkedUpdateProgress) => void,
    chunkSize: number = 150,
    reason?: string
  ): Promise<ChunkedUpdateResult> => {
    // Split updates into chunks
    const chunks: { id: number; points: number }[][] = [];
    for (let i = 0; i < updates.length; i += chunkSize) {
      chunks.push(updates.slice(i, i + chunkSize));
    }

    const totalChunks = chunks.length;
    let totalUpdated = 0;
    let totalFailed = 0;
    const allUpdatedIds: number[] = [];
    const allFailed: { id: number; error: string }[] = [];

    // Process each chunk sequentially with retry logic
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      let retryCount = 0;
      const maxRetries = 1;
      let chunkSuccess = false;

      while (!chunkSuccess && retryCount <= maxRetries) {
        try {
          const result = await distributorsApi.batchUpdatePoints(chunk, reason);
          
          totalUpdated += result.updated_count;
          totalFailed += result.failed_count;
          allUpdatedIds.push(...result.updated_ids);
          if (result.failed) {
            allFailed.push(...result.failed);
          }

          chunkSuccess = true;

          // Report progress
          if (onProgress) {
            onProgress({
              currentChunk: chunkIndex + 1,
              totalChunks,
              processedRecords: (chunkIndex + 1) * chunkSize,
              totalRecords: updates.length,
              successCount: totalUpdated,
              failedCount: totalFailed,
            });
          }
        } catch (error) {
          retryCount++;
          if (retryCount > maxRetries) {
            // Mark all items in this chunk as failed
            chunk.forEach(item => {
              allFailed.push({
                id: item.id,
                error: `Chunk ${chunkIndex + 1} failed after ${maxRetries} retries: ${error instanceof Error ? error.message : 'Unknown error'}`
              });
            });
            totalFailed += chunk.length;

            // Report progress even on failure
            if (onProgress) {
              onProgress({
                currentChunk: chunkIndex + 1,
                totalChunks,
                processedRecords: (chunkIndex + 1) * chunkSize,
                totalRecords: updates.length,
                successCount: totalUpdated,
                failedCount: totalFailed,
              });
            }
            
            chunkSuccess = true; // Exit retry loop and continue with next chunk
          } else {
            // Wait before retry (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          }
        }
      }
    }

    return {
      success: totalFailed === 0,
      totalUpdated,
      totalFailed,
      allUpdatedIds,
      allFailed,
      partialSuccess: totalUpdated > 0 && totalFailed > 0,
    };
  },

  getDistributors: async (searchQuery: string = ''): Promise<Distributor[]> => {
    const url = new URL(`${API_BASE_URL}/distributors/`, window.location.origin);
    if (searchQuery) {
      url.searchParams.append('search', searchQuery);
    }
    const response = await fetch(url.toString(), {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch distributors');
    const data = await response.json();
    // Handle both array and paginated response formats
    return Array.isArray(data) ? data : (data.results || []);
  },
  getAllDistributors: async (filters?: { search?: string; showArchived?: boolean }): Promise<Distributor[]> => {
    // Fetch all distributors by requesting maximum page size and iterating through pages
    let allDistributors: Distributor[] = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const url = new URL(`${API_BASE_URL}/distributors/`, window.location.origin);
      url.searchParams.append('page', page.toString());
      url.searchParams.append('page_size', '100'); // Max allowed by backend
      
      // Apply filters if provided
      if (filters?.search) {
        url.searchParams.append('search', filters.search);
      }
      if (filters?.showArchived !== undefined) {
        url.searchParams.append('show_archived', filters.showArchived.toString());
      }
      
      const response = await fetch(url.toString(), {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch distributors');
      
      const data = await response.json();
      const results = Array.isArray(data) ? data : (data.results || []);
      
      allDistributors = [...allDistributors, ...results];
      
      // Check if there are more pages (pagination response has 'next' field)
      hasMore = !Array.isArray(data) && data.next !== null;
      page++;
    }
    
    return allDistributors;
  },
  getAll: async (): Promise<Distributor[]> => {
    const response = await fetch(`${API_BASE_URL}/distributors/`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch distributors');
    return response.json();
  },
  getListAll: async (): Promise<{id: number; name: string; brand: string; sales_channel: string}[]> => {
    const response = await fetch(`${API_BASE_URL}/distributors/list_all/`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch distributors list');
    return response.json();
  },
  create: async (data: Omit<Distributor, 'id' | 'date_added'>): Promise<Distributor> => {
    const response = await fetch(`${API_BASE_URL}/distributors/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create distributor');
    return response.json();
  },
  // Alias for backwards compatibility
  createDistributor: async (data: Omit<Distributor, 'id' | 'date_added' | 'is_archived' | 'date_archived' | 'archived_by' | 'archived_by_username' | 'created_at' | 'updated_at'>): Promise<Distributor> => {
    const response = await fetch(`${API_BASE_URL}/distributors/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create distributor');
    return response.json();
  },
  update: async (id: number, data: Partial<Distributor>): Promise<Distributor> => {
    const response = await fetch(`${API_BASE_URL}/distributors/${id}/`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update distributor');
    return response.json();
  },
  // Alias for backwards compatibility
  updateDistributor: async (id: number, data: Partial<Distributor>): Promise<Distributor> => {
    const response = await fetch(`${API_BASE_URL}/distributors/${id}/`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update distributor');
    return response.json();
  },
  delete: async (id: number): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/distributors/${id}/`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete distributor');
  },
  // Alias for backwards compatibility
  deleteDistributor: async (id: number): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/distributors/${id}/`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete distributor');
  },

  getSalesVolumeTiers: async (): Promise<SalesVolumeTier[]> => {
    const response = await fetch(`${API_BASE_URL}/distributors/sales-volume-tiers/`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to fetch sales volume tiers');
    return response.json();
  },

  allocateSalesVolume: async (
    allocations: { id: number; sales_volume: number }[],
    reason?: string
  ): Promise<SalesVolumeAllocationResponse> => {
    const response = await fetch(`${API_BASE_URL}/distributors/allocate-sales-volume/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ allocations, reason: reason || '' }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to allocate sales volume points');
    }
    return data;
  },
};

export const dashboardApi = {
  getStats: async (): Promise<DashboardStats> => {
    const response = await fetch(`${API_BASE_URL}/dashboard/stats/`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      console.error("Dashboard stats error response:", response.status, error);
      throw new Error('Failed to fetch dashboard statistics');
    }
    return response.json();
  },
  getRedemptionRequests: async (limit: number = 10, offset: number = 0): Promise<PaginatedResponse<RedemptionRequest>> => {
    const url = new URL(`${API_BASE_URL}/dashboard/redemption-requests/`, window.location.origin);
    url.searchParams.append('limit', limit.toString());
    url.searchParams.append('offset', offset.toString());
    console.log("Fetching from URL:", url.toString());
    const response = await fetch(url.toString(), {
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      console.error("Dashboard requests error response:", response.status, error);
      throw new Error('Failed to fetch redemption requests');
    }
    const data = await response.json();
    console.log("Redemption requests data:", data);
    return data;
  },
  resetAllPoints: async (password: string): Promise<{ success: boolean; message: string }> => {
    // Extract CSRF token from cookies
    const csrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrftoken='))
      ?.split('=')[1] || '';

    const response = await fetch(`${API_BASE_URL}/dashboard/reset-all-points/`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...(csrfToken && { 'X-CSRFToken': csrfToken })
      },
      credentials: 'include',
      body: JSON.stringify({ password }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      console.error("Reset all points error:", response.status, error);
      throw new Error(error.message || 'Failed to reset points. Please check your password.');
    }
    return response.json();
  },
};

export interface AgentDashboardStats {
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  processed_count: number;
  agent_points: number;
  active_distributors_count: number;
  team_name: string;
  agent_name: string;
}

export const agentDashboardApi = {
  getStats: async (): Promise<AgentDashboardStats> => {
    const response = await fetch(`${API_BASE_URL}/agent/dashboard/stats/`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      console.error("Agent dashboard stats error response:", response.status, error);
      throw new Error('Failed to fetch agent dashboard statistics');
    }
    return response.json();
  },
};

export interface ApproverDashboardStats {
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  processed_count: number;
  team_count: number;
  team_names: string[];
  approver_name: string;
}

export const approverDashboardApi = {
  getStats: async (): Promise<ApproverDashboardStats> => {
    const response = await fetch(`${API_BASE_URL}/approver/dashboard/stats/`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      console.error("Approver dashboard stats error response:", response.status, error);
      throw new Error('Failed to fetch approver dashboard statistics');
    }
    return response.json();
  },
};